import { AxiosResponse } from "axios";
import { create } from "zustand";

import { FreeSpinsSyncResult } from "@/v2/types/FreeSpinsSync/FreeSpinsSyncResult.interface";

import {
  FreeSpinsRetrieval,
  FreeSpinsRetrievalOutcome,
  FreeSpinsRetrievalTarget,
} from "../types/FreeSpinsRetrieval";
import { SYNC_REFUSED_STATUS } from "../utils/constants";
import { getErrorResponseMessage, getErrorResponseStatus } from "../utils/getErrorResponseMessage";
import { invalidateFreeSpinsValues } from "../utils/invalidateFreeSpinsValues";
import { isPending } from "../utils/isPending";
import { isSameTarget } from "../utils/isSameTarget";

/**
 * FS retrievals, each kept until its result is dismissed, which blocks a second
 * launch. They survive leaving the page, and results queue while one is shown.
 */
export const useFreeSpinsRetrievalStore = create<FreeSpinsRetrievalStoreProps>()((set, get) => {
  const finishRetrieval = (target: FreeSpinsRetrievalTarget, outcome: FreeSpinsRetrievalOutcome) =>
    set((state) => ({
      retrievals: state.retrievals.map((retrieval) =>
        isSameTarget(retrieval.target, target) ? { ...retrieval, outcome } : retrieval,
      ),
      shown: state.shown ?? target,
    }));

  return {
    retrievals: [],
    shown: null,

    launch: async ({ target, request, onRefused }) => {
      // Latest state, so two calls in a row launch once.
      if (isPending(get().retrievals, target)) return;

      set((state) => ({
        retrievals: [...state.retrievals, { target, outcome: null }],
        shown: target,
      }));

      try {
        const { data } = await request();
        finishRetrieval(target, { status: "done", result: data });
        invalidateFreeSpinsValues();
      } catch (error) {
        finishRetrieval(target, { status: "error", errorMessage: getErrorResponseMessage(error) });
        if (getErrorResponseStatus(error) === SYNC_REFUSED_STATUS) onRefused?.();
      }
    },

    close: () =>
      set(({ retrievals, shown }) => {
        const shownRetrieval = retrievals.find((retrieval) =>
          isSameTarget(retrieval.target, shown),
        );
        // Drop a seen result; a running one only hides.
        const remaining = shownRetrieval?.outcome
          ? retrievals.filter((retrieval) => retrieval !== shownRetrieval)
          : retrievals;
        const nextFinished = remaining.find(
          (retrieval) => retrieval.outcome && retrieval !== shownRetrieval,
        );

        return { retrievals: remaining, shown: nextFinished?.target ?? null };
      }),
  };
});

interface LaunchParams {
  target: FreeSpinsRetrievalTarget;
  /** Synchronizes the target. */
  request: () => Promise<AxiosResponse<FreeSpinsSyncResult>>;
  /** Answered 400: retrieval is unavailable for the integrator. */
  onRefused?: () => unknown;
}

interface FreeSpinsRetrievalStoreProps {
  retrievals: FreeSpinsRetrieval[];
  /** The retrieval the modal shows; `null` when closed. */
  shown: FreeSpinsRetrievalTarget | null;
  launch: (params: LaunchParams) => Promise<void>;
  close: () => void;
}
